import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from 'components/elements/Form/Input/input';
import IntegrationPreferencesIcon from 'assets/Icons/sidebarIcons/IntegrationPreferencesIcon';
import { useQuery } from 'react-query';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import useSavePreference from './crud/useSave';
import { IntegrationPreferenceProps } from 'services/hooks/integration-preference-service';
import IntegrationService from 'services/hooks/integration-service';
import SelectEntity from 'components/structure/Common/ListsEntity/SelectEntity';
import Modal from 'components/elements/Modal';
import PrimaryButton from 'components/elements/Form/button/primaryButton';

type CreateProps = {
  entityName: string;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setEditUUID?: React.Dispatch<React.SetStateAction<string>>;
  createQuery: any;
  updateQuery: any;
  editQuery?: any;
  editUUID?: string;
  queryName: string;
};

// validation
const CreatePreferenceSchema = yup.object().shape({
  integrationUuid: yup.string().required('Integration is required')
});

export default function Save({
  entityName,
  showModal,
  setEditUUID,
  setShowModal,
  createQuery,
  updateQuery,
  editQuery,
  editUUID,
  queryName
}: CreateProps) {
  const [getPreferenceResult, setGetPreferenceResult] = useState<IntegrationPreferenceProps | null>(
    null
  );
  const [disabled, setdisabled] = useState(false);

  const { isSuccess, data, refetch } = useQuery<IntegrationPreferenceProps, Error>(
    'preference',
    () => editQuery(editUUID),
    {
      enabled: editUUID ? true : false,
      onError: (err: any) => {
        console.log(err.response?.data || err);
      }
    }
  );

  const methods = useForm({
    defaultValues: getPreferenceResult
      ? {
          uuid: getPreferenceResult?.uuid,
          emailRequired: getPreferenceResult?.emailRequired,
          requiresSecondAuth: getPreferenceResult?.requiresSecondAuth,
          integrationUuid: getPreferenceResult?.integrationUuid
        }
      : {
          uuid: '',
          emailRequired: '',
          requiresSecondAuth: '',
          integrationUuid: ''
        },
    resolver: yupResolver(CreatePreferenceSchema)
  });

  useEffect(() => {
    if (data && editUUID) {
      refetch();
      setGetPreferenceResult(data);
      methods.reset(data);
    }
  }, [data, editUUID, methods, refetch]);

  const savePreferenceMutation = useSavePreference({
    createQuery,
    updateQuery,
    queryName,
    onSuccess: () => {
      setShowModal(false);
      setdisabled(false);
      if (setEditUUID) setEditUUID('');
    }
  });

  const onSubmit = async (e: IntegrationPreferenceProps) => {
    setdisabled(true);

    savePreferenceMutation.mutate({
      uuid: e?.uuid,
      emailRequired: e?.emailRequired,
      requiresSecondAuth: e?.requiresSecondAuth,
      integrationUuid: e?.integrationUuid
    });
  };

  return !editUUID || isSuccess ? (
    <>
      <Modal
        isOpen={showModal}
        onRequestClose={() => {
          setShowModal(false);
          if (setEditUUID) setEditUUID('');
        }}
        title={entityName}
      >
        <form onSubmit={methods.handleSubmit(onSubmit)} className={`text-left items-start`}>
          <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4 mr-8'}>
            <SelectEntity
              Controller={Controller}
              control={methods.control}
              label="Choose Integration"
              placeholder="Choose Integration"
              queryname="integration"
              name="integrationUuid"
              errors={methods.formState.errors}
              query={() => IntegrationService.findAllIntegrations()}
              expectedLabel="IntegrationPreference/Name"
              multi={false}
              defaultValue={getPreferenceResult?.integrationUuid}
            />
            <Input
              label="Email"
              name="emailRequired"
              type="checkbox"
              // data-testid="example"
              autoFocus
              methods={methods}
            />
            <Input
              label="Second Authentication"
              name="requiresSecondAuth"
              type="checkbox"
              // data-testid="example"
              autoFocus
              methods={methods}
            />
          </div>
          <PrimaryButton
            buttonLabel={entityName}
            // onClick={onClick}
            iconLeft={<IntegrationPreferencesIcon />}
            variant="default"
            size={'mt-4 px-6 w-auto gap-2'}
            disabled={disabled}
          />
        </form>
      </Modal>
    </>
  ) : (
    <LoadingSpinner />
  );
}
